import { useEffect } from 'react';

import { addUFOCustomMark } from '@atlaskit/react-ufo/custom-mark';

import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';
import { withTransparentErrorBoundary } from '@confluence/error-boundary/entry-points/withErrorBoundary.ts';
import { fg } from '@confluence/feature-gating';
import { useIsTransition } from '@confluence/route-manager/entry-points/RouteState.ts';

type UFOTransitionMarkFMPProps = {
	timestamp?: number;
};

/**
 * Marks FMP (First Meaningful Paint) on UFO transition interactions.
 *
 * When the `confluence_fe_deprecate_bm3` feature gate is **disabled** (legacy BM3 mode),
 * this component does nothing — FMP for transitions is handled through the BM3 pipeline
 * (although unreliably, see investigation in CCP-7117).
 *
 * When the feature gate is **enabled** (UFO mode) and we are in a transition, this
 * component places an `addUFOCustomMark('fmp', timestamp)` call. Unlike `PageLoadMarkFMP`
 * which is meant for initial page loads, this component is specifically designed for SPA
 * transitions where the platform UFO library does not natively track FMP.
 *
 * The corresponding `metric:fmp` value is computed in `additionalPayloadData.ts` by
 * reading this mark from the interaction and calculating the offset from interaction start.
 */
const UFOTransitionMarkFMPComponent = ({ timestamp }: UFOTransitionMarkFMPProps) => {
	const isTransition = useIsTransition();

	useEffect(() => {
		if (isTransition && fg('confluence_fe_deprecate_bm3')) {
			addUFOCustomMark('fmp', timestamp);
		}
	}, [isTransition, timestamp]);

	return null;
};

export const UFOTransitionMarkFMP = withTransparentErrorBoundary({
	attribution: Attribution.BACKBONE,
})(UFOTransitionMarkFMPComponent);
